import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Minus, X, ShoppingBag } from "lucide-react";
import { useCart } from "@/context/CartContext";
import Breadcrumb from "@/components/Breadcrumb";

const fmt = (n) => "€" + n.toFixed(2).replace(".", ",");

export default function Cart() {
  const { items, subtotal, discount, shipping, total, updateQty, removeItem, FREE_SHIPPING_THRESHOLD, promo, applyPromo, clearPromo } = useCart();
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  const [promoErr, setPromoErr] = useState("");

  const onApplyPromo = async () => {
    setPromoErr("");
    try {
      await applyPromo(code.trim());
      setCode("");
    } catch (e) {
      setPromoErr(e?.response?.data?.detail || "Code invalide");
    }
  };

  if (items.length === 0) {
    return (
      <>
        <Breadcrumb trail={[{ label: "Accueil", to: "/" }, { label: "Panier" }]} />
        <div className="empty-state" data-testid="cart-empty">
          <ShoppingBag size={42} color="var(--gold)" style={{ margin: "0 auto 1.5rem" }} />
          <h2>Votre panier est vide</h2>
          <p className="section-body">Découvrez nos rituels de beauté inspirés du Maroc.</p>
          <Link to="/collections/all" className="btn-primary" data-testid="cart-empty-shop">Découvrir la collection</Link>
        </div>
      </>
    );
  }

  const remaining = Math.max(0, FREE_SHIPPING_THRESHOLD - (subtotal - discount));

  return (
    <>
      <Breadcrumb trail={[{ label: "Accueil", to: "/" }, { label: "Panier" }]} />

      <div className="cart-layout" data-testid="cart-page">
        <div>
          <h1 className="section-title" style={{ marginBottom: "1.5rem" }}>Votre panier</h1>
          {items.map((it) => (
            <div className="cart-line" key={it.product_id} data-testid={`cart-line-${it.product_id}`}>
              <Link to={`/products/${it.product_id}`}>
                <img src={it.image} alt={it.name} />
              </Link>
              <div>
                <Link to={`/products/${it.product_id}`} className="cart-line-name">{it.name}</Link>
                <p className="cart-line-sub">{it.subtitle}</p>
                <div className="qty-control">
                  <button onClick={() => updateQty(it.product_id, it.quantity - 1)} aria-label="Diminuer" data-testid={`cart-dec-${it.product_id}`}><Minus size={14} /></button>
                  <span data-testid={`cart-qty-${it.product_id}`}>{it.quantity}</span>
                  <button onClick={() => updateQty(it.product_id, it.quantity + 1)} aria-label="Augmenter" data-testid={`cart-inc-${it.product_id}`}><Plus size={14} /></button>
                </div>
              </div>
              <div>
                <div className="cart-line-price">{fmt(it.price * it.quantity)}</div>
                <button className="cart-remove" onClick={() => removeItem(it.product_id)} data-testid={`cart-remove-${it.product_id}`}>
                  <X size={11} style={{ display: "inline", verticalAlign: "middle" }} /> Retirer
                </button>
              </div>
            </div>
          ))}
        </div>

        <aside className="cart-summary" data-testid="cart-summary">
          <h3>Récapitulatif</h3>
          <div className="summary-row"><span>Sous-total</span><span data-testid="cart-subtotal">{fmt(subtotal)}</span></div>

          {promo ? (
            <div className="promo-applied" data-testid="cart-promo-applied">
              <span>Code <strong>{promo.code}</strong> · -{fmt(discount)}</span>
              <button onClick={clearPromo} data-testid="cart-promo-remove">Retirer</button>
            </div>
          ) : (
            <>
              <div className="promo-box">
                <input
                  placeholder="Code promo"
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                  data-testid="cart-promo-input"
                />
                <button onClick={onApplyPromo} data-testid="cart-promo-apply">Appliquer</button>
              </div>
              {promoErr ? <p className="promo-error" data-testid="cart-promo-error">{promoErr}</p> : null}
            </>
          )}

          <div className="summary-row">
            <span>Livraison</span>
            <span data-testid="cart-shipping">{shipping === 0 ? "Offerte" : fmt(shipping)}</span>
          </div>
          {remaining > 0 ? (
            <p style={{ fontSize: "0.74rem", color: "var(--muted)", margin: "0.5rem 0" }}>
              Plus que <strong>{fmt(remaining)}</strong> pour la livraison offerte.
            </p>
          ) : null}
          <div className="summary-row total"><span>Total</span><span data-testid="cart-total">{fmt(total)}</span></div>
          <button className="btn-primary" style={{ width: "100%", marginTop: "1.5rem" }} onClick={() => navigate("/commande")} data-testid="cart-checkout-btn">
            Passer la commande
          </button>
          <Link to="/collections/all" className="btn-ghost" style={{ width: "100%", marginTop: "0.75rem" }} data-testid="cart-continue">
            Continuer mes achats
          </Link>
        </aside>
      </div>
    </>
  );
}
